/* eslint-disable react/jsx-wrap-multilines */
import React, { FC, memo, useCallback, useState } from "react";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import SearchOptions from "Components/SearchOptions";
import { areEqual, areEqualShallow } from "Utils/areEqual";
import Icon from "Components/Icons";
import { Message } from "Components/Toast";
import { setDestinationsView } from "Containers/Destinations";
import {
  setDestinationsViewTrigger,
  SetDestinationsViewTriggerType,
} from "Containers/Destinations/actions";
import { setShowSearchOptions } from "Containers/CampusMap";
import { setCurrentView } from "Containers/MapSwitcher/mapSwitcher.Actions";
import { setCurrentStop, setStopState, StopState } from "Containers/Stops";
import { motion } from "framer-motion";
import { checkIsTrailsEnabled } from "Utils/utils";
import styles from "./styles";

interface Props {
  setSearchOptionsView: (view: string) => void;
  showDestinations: boolean;
  showHideList: (trigger: SetDestinationsViewTriggerType) => void;
  trailsCount: number;
  currentStop: number;
  showSwitcher: boolean;
}
const SearchOptionsDestinations: FC<Props> = memo(
  ({
    showDestinations,
    trailsCount,
    setSearchOptionsView,
    showHideList,
    currentStop = -1, // Default to no stops
    showSwitcher,
  }: Props) => {
    const useStyles = createUseStyles(styles);
    const classes = useStyles();
    const dispatch = useDispatch();
    const { modules } = useSelector(
      (selectors: any) => selectors.locationModulesMatrix,
      areEqual
    );
    const currentView = useSelector(
      (store: any) => store.mapSwitcher.currentView
    );
    const { platform, originalSource } = useSelector(
      (store: any) => store.tour
    );
    const directionsModule = modules?.filter((obj) => {
      return obj.moduleid === "directions";
    })[0];
    const view = useSelector((store: any) => store.searchOptionsView.view);

    const showDirectionsModule = () => {
      let correctPlatform;
      if (platform === "w") correctPlatform = "web";
      if (platform === "v") correctPlatform = "vhost";
      if (!platform) correctPlatform = originalSource;
      return directionsModule[correctPlatform] === "1";
    };

    const [showPrompt, setShowPrompt] = useState(false);

    const backToTrails = useCallback(() => {
      if (currentView === "campusmap") {
        setShowPrompt(true);
        dispatch(setCurrentView("mapview", false));
      }
      setSearchOptionsView("trails");
      dispatch(setShowSearchOptions(true));

      // close stop popup (<Scene />) when Trail is opened
      if (currentStop > -1) {
        dispatch(setStopState([currentStop], StopState.Visited));
        dispatch(setCurrentStop(-1));
      }
    }, [dispatch, currentStop, setSearchOptionsView, currentView]);

    const backToDestinations = useCallback(() => {
      dispatch(setDestinationsView(false));
      dispatch(setShowSearchOptions(false));
      setSearchOptionsView("destinations");
    }, [dispatch, setSearchOptionsView]);

    const backToDirections = useCallback(() => {
      if (currentView === "campusmap") {
        setShowPrompt(true);
        dispatch(setCurrentView("mapview", false));
      }
      setSearchOptionsView("directions");
    }, [dispatch, currentView, setSearchOptionsView]);

    const showMobileView = useCallback(() => {
      dispatch(setDestinationsView(!showDestinations));
      dispatch(setShowSearchOptions(false));
      setSearchOptionsView("destinations");

      if (!showDestinations) {
        dispatch(setCurrentStop(-1));
        dispatch(setStopState([currentStop], StopState.Default));
      }
    }, [dispatch, showDestinations, currentStop, setSearchOptionsView]);

    const showTrailsOrDirections = () => {
      if (checkIsTrailsEnabled(trailsCount))
        return (
          <Icon
            key={`showDestinations-${showDestinations}-slot2-a`}
            type="trails"
            events={{
              onClick: backToTrails,
            }}
          />
        );
      if (directionsModule && showDirectionsModule())
        return (
          <Icon
            key={`showDestinations-${showDestinations}-slot2-a-b`}
            type="directions"
            events={{
              onClick: backToDirections,
            }}
            className={classes.button}
          />
        );
      return null;
    };

    const handleDestinationsClick = () => {
      showHideList(setDestinationsViewTrigger.BUTTON_CLICK);
    };

    const slot3Animations = {
      show: {
        opacity: 1,
        width: "32px",
        transition: { ease: [0.28, 0.57, 0.2, 1], duration: 0.5 },
      },
      hide: {
        opacity: 0,
        width: 0,
        transition: { ease: [0.28, 0.57, 0.2, 1], duration: 0.5 },
        display: "none",
      },
    };

    return (
      <>
        {view === "destinations" && (
          <>
            <div className={classes.desktopDest}>
              <SearchOptions
                hasDivider={showDestinations}
                key={`showDestinations-${showDestinations}-a`}
                slot2={showTrailsOrDirections()}
                slot3={
                  <motion.div
                    style={{ overflow: "hidden" }}
                    variants={slot3Animations}
                    initial={showDestinations ? "hide" : "show"}
                    animate={showDestinations ? "show" : "hide"}
                  >
                    <Icon
                      key={`showDestinations-${showDestinations}-slot3-a`}
                      type="close"
                      events={{
                        onClick: backToDestinations,
                        onKeyDown: (event) => {
                          if (event.key === "Enter") {
                            backToDestinations();
                          }
                        },
                      }}
                    />
                  </motion.div>
                }
                currentLabel={
                  <Icon
                    key={`showDestinations-${showDestinations}-currentLabel-a`}
                    text="Destinations"
                    type="currentLabel"
                    events={{
                      onClick: handleDestinationsClick,
                    }}
                    className={classes.button}
                  />
                }
              />
            </div>
            <div className={classes.mobileDest}>
              {showDestinations && (
                <SearchOptions
                  key={`showDestinations-mobile-${showDestinations}-a`}
                  slot2={showTrailsOrDirections()}
                  slot3={
                    <motion.div
                      style={{ overflow: "hidden" }}
                      variants={slot3Animations}
                      initial="hide"
                      animate="show"
                    >
                      <Icon
                        key={`showDestinations-mobile-${showDestinations}-slot3-a`}
                        type="close"
                        events={{
                          onClick: showMobileView,
                        }}
                      />
                    </motion.div>
                  }
                  currentLabel={
                    <Icon
                      key={`showDestinations-mobile-${showDestinations}-currentLabel-a`}
                      text="Destinations"
                      type="currentLabel"
                      className={classes.button}
                      events={{
                        onClick: handleDestinationsClick,
                      }}
                    />
                  }
                />
              )}
            </div>
          </>
        )}

        {showSwitcher && showPrompt ? (
          <Message
            title="Google Maps activated for Trails"
            message="Trails & Directions are only accessible via the Google Maps view."
            onClose={() => setShowPrompt(false)}
          />
        ) : null}
      </>
    );
  },
  areEqualShallow
);

export default SearchOptionsDestinations;
